import { afterNextRender, inject, Injectable, Injector } from '@angular/core';
import type {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

import { NavCacheService } from './nav-cache.service';
import { NavRouteService } from './nav-route.service';
import { NavStore } from './nav.store';
import { NavIntent, SCROLL_RESTORE_ATTEMPTS, type ScrollOffset } from './nav.types';
import {
  captureScroll,
  findLeafSnapshot,
  findRootSnapshot,
  isRootSnapshot,
  restoreScroll,
} from './nav.util';

@Injectable()
export class NavReuseStrategy implements RouteReuseStrategy {
  private readonly injector = inject(Injector);
  private readonly navCacheService = inject(NavCacheService);

  // Lazy lookup breaks the Router -> NavStore -> Router cycle.
  private _navStore?: InstanceType<typeof NavStore>;
  private _navRouteService?: NavRouteService;
  private pendingScroll: readonly ScrollOffset[] = [];

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (!this.isCacheBoundary(route)) return false;

    const tab = this.navStore.activeTab();
    const key = this.getSnapshotKey(route);
    const detach = !!tab && key === tab.key;

    // Read before removal. The browser resets scroll position once the view leaves the document.
    if (detach) this.pendingScroll = captureScroll(document.getElementById('ks-tab-panel'));

    return detach;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    // Detach stores the source; Angular's post-retrieve null clears the target.
    if (handle) {
      const sourceTabId = this.navStore.activeTabId();
      const scroll = this.pendingScroll;

      this.pendingScroll = [];

      if (sourceTabId) this.navCacheService.set(sourceTabId, handle, scroll);

      return;
    }

    const targetTabId = this.navRouteService.getCurrentContext().tabId;

    if (targetTabId) this.navCacheService.release(targetTabId);
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!this.isCacheBoundary(route)) return false;

    const targetTabId = this.navRouteService.getCurrentContext().tabId;

    if (!targetTabId) return false;

    const tab = this.navStore.getTabById(targetTabId);

    if (!tab) {
      this.navCacheService.delete(targetTabId);

      return false;
    }

    if (!this.navCacheService.has(targetTabId)) return false;

    const targetUrl = this.getSnapshotUrl(route);

    // A cached handle owns the exact URL last displayed by its tab.
    if (!targetUrl || targetUrl !== tab.routeUrl) {
      this.navCacheService.delete(targetTabId);

      return false;
    }

    return true;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!this.isCacheBoundary(route)) return null;

    const targetTabId = this.navRouteService.getCurrentContext().tabId;

    if (!targetTabId) return null;

    if (this.navCacheService.peekScroll(targetTabId).length) {
      afterNextRender(() => this.restoreTabScroll(targetTabId, 0), { injector: this.injector });
    }

    // shouldAttach already validated expiry for this synchronous router read.
    return this.navCacheService.peek(targetTabId);
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (future.routeConfig !== curr.routeConfig) return false;

    const { intent, tabId: targetTabId } = this.navRouteService.getCurrentContext();
    const activeTabId = this.navStore.activeTabId();

    // Rooted tabs reuse only while their identity key is unchanged.
    if (this.isRootRoute(future) || this.isRootRoute(curr)) {
      if (targetTabId && activeTabId && targetTabId !== activeTabId) return false;

      return this.getSnapshotKey(future) === this.getSnapshotKey(curr);
    }

    if (!this.isLeafRoute(future) || !this.isLeafRoute(curr)) return true;

    if (targetTabId && activeTabId && targetTabId !== activeTabId) return false;

    const futureKeyObj = this.navRouteService.createKeyFromSnapshot(future);
    const currKeyObj = this.navRouteService.createKeyFromSnapshot(curr);

    if (!futureKeyObj || !currKeyObj) return true;

    if (
      this.navRouteService.getKeyString(futureKeyObj) ===
      this.navRouteService.getKeyString(currKeyObj)
    )
      return true;

    if (futureKeyObj.path !== currKeyObj.path) return false;

    if (targetTabId) return intent !== NavIntent.TabSwitch;

    const futureKey = this.navRouteService.getKeyString(futureKeyObj);
    const activeKey = this.navStore.activeTab()?.key;
    const targetsAnotherTab = this.navStore.hasTabKey(futureKey) && activeKey !== futureKey;

    return intent !== NavIntent.TabSwitch && !targetsAnotherTab;
  }

  private get navStore(): InstanceType<typeof NavStore> {
    this._navStore ??= this.injector.get(NavStore);

    return this._navStore;
  }

  private get navRouteService(): NavRouteService {
    this._navRouteService ??= this.injector.get(NavRouteService);

    return this._navRouteService;
  }

  private isLeafRoute(route: ActivatedRouteSnapshot): boolean {
    return !route.firstChild;
  }

  private isRootRoute(route: ActivatedRouteSnapshot): boolean {
    return isRootSnapshot(route);
  }

  // Rooted tabs cache at the root; other tabs cache at the leaf.
  private isCacheBoundary(route: ActivatedRouteSnapshot): boolean {
    if (this.isRootRoute(route)) return true;
    if (!this.isLeafRoute(route)) return false;

    return !isRootSnapshot(findRootSnapshot(route));
  }

  private getSnapshotKey(route: ActivatedRouteSnapshot): string | null {
    return this.navRouteService.createTabIdentityFromSnapshot(route)?.key ?? null;
  }

  private getSnapshotUrl(route: ActivatedRouteSnapshot): string | null {
    const keyObj = this.navRouteService.createKeyFromSnapshot(findLeafSnapshot(route));

    return keyObj ? this.navRouteService.getKeyUrl(keyObj) : null;
  }

  // Retrieval runs before the view is laid out, including for the tab being left.
  // Apply the position only once its container is connected and can hold it.
  private restoreTabScroll(tabId: string, attempt: number): void {
    const scroll = this.navCacheService.peekScroll(tabId);

    if (!scroll.length) return;

    const connected = scroll.filter(({ element }) => element.isConnected);

    if (!connected.length) return;

    const ready = connected.every(
      ({ element, top, left }) =>
        element.scrollHeight - element.clientHeight >= top &&
        element.scrollWidth - element.clientWidth >= left,
    );

    if (!ready && attempt < SCROLL_RESTORE_ATTEMPTS) {
      requestAnimationFrame(() => this.restoreTabScroll(tabId, attempt + 1));

      return;
    }

    restoreScroll(connected);
    this.navCacheService.clearScroll(tabId);
  }
}
